import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Subject, takeUntil, tap } from 'rxjs';
import { PasswordEvaluator } from '../password-evaluator';
import { passwordValidator } from '../password.validator';
import { ADPResourceService, StorageService } from 'digital-core';
import { buildMatchFieldValidator } from '../../match-field/match-field.validator';
import { PGHOktaService } from 'src/app/core/services/pghokta.service';

export interface PasswordFormControls {
  currentPassword?: FormControl<string>;
  password?: FormControl<string>;
  confirmPassword?: FormControl<string>;
}

interface PasswordRequirementInfo {
  contentKey: string;
  isRequirementSatisfied: boolean;
}

@Component({
  selector: 'pgh-password-form',
  templateUrl: './password-form.component.html',
  styleUrls: ['./password-form.component.scss']
})
export class PasswordFormComponent implements OnInit, OnDestroy {
  private onDestroy$ = new Subject<void>();

  public passwordRequirements$ = new BehaviorSubject(this.evaluatePasswordAgainstRequirements(''));

  public currentPasswordHidden = true;
  public passwordHidden = true;
  public confirmPasswordHidden = true;
  public username = '';

  @Input('forFormGroup')
  public passwordForm: FormGroup<PasswordFormControls>;

  @Input()
  public showCurrentPasswordField = false;

  @Input()
  public passwordLabelSFKey: string;

  @Input()
  public confirmPasswordLabelSFKey: string;
  checkUsernameInPassword: boolean;

  public constructor(
    public resourceService: ADPResourceService,
    private pghOktaService: PGHOktaService,
    private storageService: StorageService

  ) { }

  public ngOnInit() {


    this.checkUsernameInPassword = this.resourceService.isPortalSettingTrue('Global_CheckUsernameInPassword');
    const passwordControl = new FormControl('', [Validators.required, passwordValidator]);
    this.passwordForm.addControl('password', passwordControl);
    this.passwordForm.addControl('confirmPassword', new FormControl('', [Validators.required, buildMatchFieldValidator(passwordControl, 'PasswordForm_ConfirmPasswordDoesNotMatch_Message')]));
    if (this.showCurrentPasswordField) {
      this.passwordForm.addControl('currentPassword', new FormControl('', [Validators.required]));
    }

    // Emit new PasswordRequirementInfo array on password field change as well as check the confirmPassword field again for validity
    this.passwordForm.controls.password.valueChanges.pipe(
      takeUntil(this.onDestroy$),
      tap(newPassword => {
        this.passwordForm.controls.confirmPassword.updateValueAndValidity();
        //this.passwordForm.controls.password.updateValueAndValidity();

        this.passwordRequirements$.next(this.evaluatePasswordAgainstRequirements(newPassword));

      }),
    ).subscribe();
    this.setUserName();
  }

  public updateField() {
      this.passwordForm.controls.password.updateValueAndValidity();
      this.passwordForm.updateValueAndValidity();

  }

  public async setUserName() {
    if(this.pghOktaService.isTokenValid())
        {
            let userClaims = await this.pghOktaService.getUser();
            this.username = userClaims.email;
        } else {
            this.username = this.storageService.getSession('RegEmail');
        }
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.passwordRequirements$.complete();
  }

  public toggleCurrentPasswordVisibility() {
    this.currentPasswordHidden = !this.currentPasswordHidden;
  }
  public togglePasswordVisibility() {
    this.passwordHidden = !this.passwordHidden;
  }
  public toggleConfirmPasswordVisibility() {
    this.confirmPasswordHidden = !this.confirmPasswordHidden;
  }

  private evaluatePasswordAgainstRequirements(password: string): PasswordRequirementInfo[] {
    return [
      {
        contentKey: 'PasswordForm_PasswordMinLength_Text',
        isRequirementSatisfied: PasswordEvaluator.isLongEnough(password),
      },
      {
        contentKey: 'PasswordForm_PasswordRequiresLowercase_Text',
        isRequirementSatisfied: PasswordEvaluator.hasLowerCaseLetters(password),
      },
      {
        contentKey: 'PasswordForm_PasswordRequiresUppercase_Text',
        isRequirementSatisfied: PasswordEvaluator.hasUpperCaseLetters(password),
      },
      {
        contentKey: 'PasswordForm_PasswordRequiresNumber_Text',
        isRequirementSatisfied: PasswordEvaluator.hasDigits(password),
      },
      {
        contentKey: 'PasswordForm_PasswordRequiresSymbol_Text',
        isRequirementSatisfied: PasswordEvaluator.hasSpecialCharacters(password),
      },
      {
        contentKey: 'PasswordForm_PasswordNoPersonalInfo_Text',
        isRequirementSatisfied: PasswordEvaluator.doesNotContainUsername(this.username, password, this.checkUsernameInPassword),
      },
    ];
  }

}
